import React, { useState, useRef, useEffect, useCallback } from "react";

import DataTable from "../../components/common/DataTable/DataTable";
import addIcon from "../../assets/images/icons/addIcon.png";
import filterIcon from "../../assets/images/icons/filterIcon.svg";
import searchIcon from "../../assets/images/icons/search.png";
import deleteIcon from "../../assets/images/icons/Delete.svg";
import FilterCoreModal from "../../components/common/FilterCoreModal/FilterCoreModal";
import FilterForm from "../../components/MyTasks/FilterForm/FilterForm";
import {
  useGetIncidentTaskCreationTriggersQuery,
  useGetTriggerQuery,
  useGetCreationInfoQuery,
  useCreateIncidentTaskTriggerMutation,
  useUpdateIncidentTaskTriggerMutation,
  useDeleteIncidentTaskTriggerMutation,
} from "./taskAutomationSlice";
import { useGetLibraryTasksQuery } from "../TaskLibrary/taskLibrarySlice";
import { nanoid } from "@reduxjs/toolkit";
import viewIcon from "../../assets/images/icons/view.png";
import editIcon from "../../assets/images/icons/edit.png";
import IncidentReportingModal from "../../components/Incidents/IncidentReportingModal/IncidentReportingModal";
import DataForm from "../../components/common/DataForm/DataForm";
import { Loader } from "../../components/common/Loader/Loader";
import { useGetIncidentGroupsQuery } from "../Incidents/IncidentSlice";
import toast, { Toaster } from "react-hot-toast";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmationModal/DeleteConfirmationModal";
import { sanitizeInputValue } from "../../utils/commonMethods";

const TaskAutomation = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [incidentTypeData, setIncidentTypeData] = useState([]);
  const [libraryTasksData, setLibraryTasksData] = useState([]);
  const [assignedToVariables, setAssignedToVariables] = useState([]);
  const [postTasks, setPostTasks] = useState([]);
  const [selectedTrigger, setSelectedTrigger] = useState(0);
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedData, setSelectedData] = useState({});
  const [readOnly, setReadOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const incidentGroupsData = useRef();
  const creationInfo = useRef();
  const taskDescription = useRef("");
  const setTaskDescription = (newValue) => {
    taskDescription.current = newValue;
  };
  const setCreationInfo = (newValue) => {
    creationInfo.current = newValue;
  };
  const setIncidentGroupsData = (newValue) => {
    incidentGroupsData.current = newValue;
  };

  const initialFormStructure = {
    pages: [
      {
        name: "page1",
        elements: [
          {
            type: "dropdown",
            name: "taskId",
            title: "Select the Task",
            isRequired: true,
          },
          {
            type: "comment",
            name: "taskDescription",
            title: "Task Description",
            isRequired: true,
          },
          {
            type: "dropdown",
            name: "triggerId",
            title: "Selecting the Trigger for creating the task",
            isRequired: true,
            choices: ["Item 1", "Item 2", "Item 3"],
          },
          {
            type: "dropdown",
            name: "postCompletionTaskId",
            visibleIf: "{triggerId} = 2",
            title: "Select the Post completion Task",
            isRequired: true,
          },
          {
            type: "dropdown",
            name: "incidentGroupId",
            visibleIf: "{triggerId} <> 2",
            title: "Select Incident Group",
            isRequired: true,
          },
          {
            type: "dropdown",
            name: "incidentTypeId",
            visibleIf: "{IncidentGroupId} notempty and {triggerId} <> 2",
            title: "Select the Incident Type",
          },
          {
            type: "dropdown",
            name: "incidentSubTypeId",
            visibleIf:
              "{incidentTypeId} notempty and {IncidentGroupId} notempty and {triggerId} <> 2",
            title: "Select the incident Sub Type",
          },
          {
            type: "paneldynamic",
            name: "conditions",
            title: "Conditions",
            state: "collapsed",
            templateElements: [
              {
                type: "dropdown",
                name: "questionKey",
                title: "Question",
                isRequired: true,
                choices: [],
              },
              {
                type: "dropdown",
                name: "conditionId",
                startWithNewLine: false,
                title: "Condition",
                isRequired: true,
                choices: [],
              },
              {
                type: "text",
                name: "value",
                startWithNewLine: false,
                isRequired: true,
                title: "Value",
              },
            ],
            panelCount: 0,
            noEntriesText:
              "No Conditions yet.\nClick the button below to add new Conditions.",
            panelAddText: "Add Condition",
            panelRemoveText: "Remove Condition",
          },
          {
            type: "tagbox",
            name: "assignedTo",
            title: "Assigned To",
            choicesLazyLoadEnabled: true,
          },
        ],
      },
      {
        name: "page2",
        elements: [
          {
            type: "text",
            name: "dueInDays",
            title: "How long after creation is it due? (in days)",
            isRequired: true,
            validators: [
              {
                type: "regex",
                text: "Days cannot be Decimal",
                regex: "^[0-9]+$",
              },
            ],
            inputType: "number",
            min: 0,
            step: 1,
          },
          {
            type: "boolean",
            name: "allowedDocumentsToLink",
            title: "Allow documents to be attached",
            isRequired: true,
          },
        ],
      },
    ],
    widthMode: "responsive",
  };

  const [formStructure, setFormStructure] = useState(initialFormStructure);

  const [
    incidentTaskCreationTriggersData,
    setIncidentTaskCreationTriggersData,
  ] = useState([]);
  const [columns, setColumns] = useState([]);

  const {
    data: incidentTaskCreationTriggers,
    isLoading: incidentTaskCreationTriggersIsLoading,
    isSuccess: incidentTaskCreationTriggersIsSuccess,
    error: incidentTaskCreationTriggersError,
  } = useGetIncidentTaskCreationTriggersQuery();

  const { data: triggers, isSuccess: triggersIsSuccess } = useGetTriggerQuery();

  const {
    data: incidentGroups,
    isLoading: incidentGroupsIsLoading,
    isSuccess: incidentGroupsIsSuccess,
  } = useGetIncidentGroupsQuery();

  const { data: libraryTasks, isSuccess: libraryTasksIsSuccess } =
    useGetLibraryTasksQuery();

  const {
    data: creationInfoData,
    isLoading: creationInfoDataIsLoading,
    isFetching: creationInfoDataIsFetching,
    error: creationInfoDataError,
  } = useGetCreationInfoQuery();

  const [
    createIncidentTaskTrigger,
    { isLoading: createIncidentTaskTriggerIsLoading },
  ] = useCreateIncidentTaskTriggerMutation();
  const [
    updateIncidentTaskTrigger,
    { isLoading: updateIncidentTaskTriggerIsLoading },
  ] = useUpdateIncidentTaskTriggerMutation();
  const [
    deleteIncidentTaskTrigger,
    { isLoading: deleteIncidentTaskTriggerIsLoading },
  ] = useDeleteIncidentTaskTriggerMutation();

  useEffect(() => {
    if (creationInfoData) {
      setCreationInfo(creationInfoData);
    }
    if (creationInfoDataError) {
      toast.error(
        `${
          creationInfoDataError?.data?.Message ||
          creationInfoDataError?.data?.title
        }`
      );
    }
  }, [creationInfoData, creationInfoDataError]);

  useEffect(() => {
    if (creationInfoDataIsLoading) return;
    if (creationInfoData) {
      const updatedFormStructure = { ...formStructure };
      // const userData = users.ids.map((id) => {
      //   return users.entities[id];
      // });
      // setPageSize(userData[0]?.pageSettings?.totalRecordsCount);
      // const assignees = userData.map((d) => ({
      //   id: `userId-${d.id}`,
      //   name: `${d.firstName} ${d.lastName}`,
      // }));
      const assignedToVariables = (
        creationInfoData?.assignedToVariables || []
      ).map((assignToVariable) => ({
        text: assignToVariable.name,
        value: `assignedToVariableId-${assignToVariable.id}`,
      }));

      setAssignedToVariables(assignedToVariables);

      // const allUsers = [...assignees, ...assignedToVariables];

      const conditionsSection = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "conditions"
      );

      // const assigneeDropdown = updatedFormStructure.pages[0].elements.find(
      //   (element) => element.name === "assignedTo"
      // );

      // if (assigneeDropdown) {
      //   assigneeDropdown.choices = allUsers.map((assignee) => ({
      //     value: assignee.id,
      //     text: assignee.name,
      //   }));
      // }
      if (conditionsSection) {
        conditionsSection.templateElements[0].choices = (
          creationInfoData?.questions || []
        ).map((question) => ({
          value: `${question?.key}#${question.dataType}`,
          text: question?.name,
        }));
        conditionsSection.templateElements[1].choices =
          creationInfoData.allowedConditions.map((allowedCondition) => ({
            value: `${allowedCondition?.id}#${allowedCondition.dataType}`,
            text: allowedCondition?.name,
          }));
      }

      setFormStructure(updatedFormStructure);
    }
  }, [creationInfoData, creationInfoDataIsLoading]);

  const setTypes = useCallback((props) => {
    const types =
      incidentGroupsData &&
      incidentGroupsData?.current?.filter(
        (group) => group.id === props.incidentGroupId
      )[0]?.types;
    setIncidentTypeData(types);
    const subTypes =
      types &&
      types?.filter((type) => type.id === props.incidentTypeId)[0]?.subTypes;
    const updatedFormStructure = { ...formStructure };
    const incidentTypDropdown = updatedFormStructure.pages[0].elements.find(
      (element) => element.name === "incidentTypeId"
    );
    const incidentSubTypDropdown = updatedFormStructure.pages[0].elements.find(
      (element) => element.name === "incidentSubTypeId"
    );

    if (incidentTypDropdown) {
      incidentTypDropdown.choices = types?.map((type) => ({
        value: type.id,
        text: type.description,
      }));
    }
    if (incidentSubTypDropdown) {
      incidentSubTypDropdown.choices = subTypes?.map((type) => ({
        value: type.id,
        text: type.description,
      }));
    }

    setFormStructure(updatedFormStructure);
  }, []);

  const handleClick = useCallback(
    (props, type) => {
      const formattedConditons = props?.conditions?.map((condition) => {
        const questionDataType = creationInfo.current?.questions.find(
          (q) => q.key === condition?.questionKey
        )?.dataType;
        const conditionDataType = creationInfo.current?.allowedConditions.find(
          (c) => c.id === condition?.conditionId
        )?.dataType;

        return {
          ...condition,
          questionKey: `${condition?.questionKey}#${questionDataType}`,
          conditionId: `${condition?.conditionId}#${conditionDataType}`,
        };
      });

      const data = {
        id: props?.id,
        taskDescription: props?.taskDescription,
        incidentGroupId: props?.incidentGroupId,
        incidentTypeId: props?.incidentTypeId,
        incidentSubTypeId: props?.incidentSubTypeId,
        taskId: props?.taskId,
        dueInDays: props?.dueInDays,
        allowedDocumentsToLink: props?.allowedDocumentsToLink,
        triggerId: props?.triggerId,
        postCompletionTaskId: props?.postCompletionTaskId,
        name: props?.name,
        assignedTo: props?.assignedTo?.map((assignedTo) => {
          if (assignedTo.assigneeUser) {
            return `userId-${assignedTo.assigneeUser.id}`;
          }
          return `assignedToVariableId-${assignedTo.assignedToVariable.id}`;
        }),
        conditions: formattedConditons,
      };

      switch (type) {
        case "edit":
          setIsUpdate(true);
          setReadOnly(false);
          break;
        case "delete":
          setShowModal(true);
          setSelectedData(data);
          return;
        default:
          setIsUpdate(false);
          setReadOnly(true);
      }
      setTypes(data);
      setSelectedData(data);
      setModalOpen(true);
    },
    [creationInfo, setTypes]
  );

  const generateColumns = useCallback(
    (data) => {
      if (data.length <= 0) {
        return;
      }

      const optionsConfig = {
        key: nanoid(),
        field: "options",
        headerText: "Options",
        template: (props) => (
          <div>
            <button
              className="e-btn e-flat option-btn"
              onClick={() => handleClick(props, "view")}
            >
              <img src={viewIcon} width={30} alt="view icon" />
            </button>
            <button
              className="e-btn e-flat option-btn"
              onClick={() => handleClick(props, "edit")}
            >
              <img src={editIcon} width={30} alt="edit icon" />
            </button>
            <button
              className="e-btn e-flat option-btn"
              onClick={() => handleClick(props, "delete")}
            >
              <img src={deleteIcon} width={30} alt="delete icon" />
            </button>
          </div>
        ),
        width: "200px",
      };

      const columns = Object.keys(data[0])
        .map((field) => {
          if (field === "id") {
            return null;
          }
          if (field === "assignedTo") {
            return null;
          }
          if (
            field === "taskId" ||
            field === "triggerId" ||
            field === "postCompletionTaskId" ||
            field === "incidentGroupId" ||
            field === "incidentTypeId" ||
            field === "incidentSubTypeId" ||
            field === "conditions"
          ) {
            return null;
          }

          let columnConfig = {
            key: nanoid(),
            field,
            headerText: field
              .replace(/([A-Z])/g, " $1")
              .trim()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
            alignment: "left",
            template: null,
            width: "180px",
          };

          if (field === "name") {
            columnConfig.template = (props) => {
              return <p className="m-0 fw-bold">{props[field]}</p>;
            };
            columnConfig.width = "400px";
          }

          if (field === "taskDescription") {
            columnConfig.width = "500px";
          }

          if (field === "allowedDocumentsToLink") {
            columnConfig.template = (props) => {
              return (
                <p className="m-0 text-center">
                  {props[field] ? "Yes" : "No"}{" "}
                </p>
              );
            };
          }

          if (field === "dueIn") {
            columnConfig.headerText = "Due In (Days)";
          }
          if (field === "trigger") {
            columnConfig.headerText = "Type of Trigger";
          }

          return columnConfig;
        })
        .filter(Boolean);

      columns.splice(columns.length, 0, optionsConfig);
      return columns;
    },
    [handleClick]
  );

  useEffect(() => {
    if (incidentTaskCreationTriggers) {
      const data = incidentTaskCreationTriggers.ids.map((id) => {
        return incidentTaskCreationTriggers.entities[id];
      });
      setIncidentTaskCreationTriggersData(data);
      setColumns(generateColumns(data));
    }
    if (incidentTaskCreationTriggersError) {
      toast.error(
        incidentTaskCreationTriggersError?.data?.Message ||
          incidentTaskCreationTriggersError?.data?.title ||
          "Unexpected error occured while fetching task automation list...!"
      );
    }
  }, [
    incidentTaskCreationTriggers,
    incidentTaskCreationTriggersError,
    generateColumns,
  ]);

  useEffect(() => {
    if (libraryTasksIsSuccess && libraryTasks) {
      const data = libraryTasks.ids.map((id) => {
        return libraryTasks.entities[id];
      });
      setLibraryTasksData(data);
      const updatedFormStructure = { ...formStructure };
      const taskDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "taskId"
      );
      const postTaskDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "postCompletionTaskId"
      );

      setPostTasks(
        data.map((task) => ({
          value: task.id,
          text: task.name,
        }))
      );

      if (postTaskDropdown) {
        postTaskDropdown.choices = data.map((task) => ({
          value: task.id,
          text: task.name,
        }));
      }

      if (taskDropdown) {
        taskDropdown.choices = data.map((task) => ({
          value: task.id,
          text: task.name,
        }));
      }

      setFormStructure(updatedFormStructure);
    }
  }, [libraryTasksIsSuccess, libraryTasks]);

  useEffect(() => {
    if (triggersIsSuccess && triggers) {
      const data = triggers.ids.map((id) => {
        return triggers.entities[id];
      });

      const updatedFormStructure = { ...formStructure };
      const triggerDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "triggerId"
      );

      if (triggerDropdown) {
        triggerDropdown.choices = data.map((trigger) => ({
          value: trigger.id,
          text: trigger.type.description,
        }));
      }

      setFormStructure(updatedFormStructure);
    }
  }, [triggersIsSuccess, triggers]);

  useEffect(() => {
    if (incidentGroupsIsSuccess && incidentGroups) {
      const data = incidentGroups.ids.map((id) => {
        return incidentGroups.entities[id];
      });

      setIncidentGroupsData(data);
      const groupChoices = data.map((group) => ({
        value: group.id,
        text: group.description,
      }));

      const updatedFormStructure = { ...formStructure };
      const incidentGroupDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "incidentGroupId"
      );

      if (incidentGroupDropdown) {
        incidentGroupDropdown.choices = groupChoices;
      }

      setFormStructure(updatedFormStructure);
    }
  }, [incidentGroupsIsSuccess, incidentGroups]);

  const resetForm = () => {
    setFormStructure((prev) => {
      const updatedPages = prev.pages.map((page) => {
        const updatedElements = page.elements.map((element) => ({
          ...element,
          defaultValue: "",
        }));
        return { ...page, elements: updatedElements };
      });

      return { ...prev, pages: updatedPages };
    });
  };

  const handleTrigger = (_, options) => {
    const questionName = options.name;
    const newValue = options.value;

    if (questionName === "incidentGroupId" && newValue) {
      const types = incidentGroupsData.current.filter(
        (group) => group.id === newValue
      )[0].types;
      if (types) {
        const updatedFormStructure = { ...formStructure };
        const incidentTypeDropdown =
          updatedFormStructure.pages[0].elements.find(
            (element) => element.name === "incidentTypeId"
          );
        const incidentGroupDropdown =
          updatedFormStructure.pages[0].elements.find(
            (element) => element.name === "incidentGroupId"
          );

        if (incidentTypeDropdown) {
          incidentTypeDropdown.choices = types.map((incidentType) => ({
            value: incidentType.id,
            text: incidentType.description,
          }));
          if (isUpdate) {
            setSelectedData({ ...selectedData, incidentGroupId: newValue });
          } else {
            incidentGroupDropdown.defaultValue = newValue;
          }
        }

        const triggerDropdown = updatedFormStructure.pages[0].elements.find(
          (element) => element.name === "triggerId"
        );

        triggerDropdown.defaultValue = selectedTrigger;

        setFormStructure(updatedFormStructure);

        setIncidentTypeData(types);
      }
    }

    if (questionName === "incidentTypeId" && newValue) {
      const subTypes = incidentTypeData?.filter(
        (type) => type.id === newValue
      )[0].subTypes;
      if (subTypes) {
        const updatedFormStructure = { ...formStructure };

        const incidentTypeDropdown =
          updatedFormStructure.pages[0].elements.find(
            (element) => element.name === "incidentTypeId"
          );

        if (incidentTypeDropdown) {
          if (isUpdate) {
            setSelectedData({ ...selectedData, incidentTypeId: newValue });
          } else {
            incidentTypeDropdown.defaultValue = newValue;
          }
        }

        const incidentSubTypeDropdown =
          updatedFormStructure.pages[0].elements.find(
            (element) => element.name === "incidentSubTypeId"
          );
        if (incidentSubTypeDropdown) {
          incidentSubTypeDropdown.choices = subTypes.map((incidentType) => ({
            value: incidentType.id,
            text: incidentType.description,
          }));
        }

        setFormStructure(updatedFormStructure);
      }
    }

    if (questionName === "triggerId" && newValue) {
      const updatedFormStructure = { ...formStructure };

      const triggerDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "triggerId"
      );

      const taskDescriptionQuestion =
        updatedFormStructure.pages[0].elements.find(
          (element) => element.name === "taskDescription"
        );

      if (isUpdate) {
        setSelectedData({ ...selectedData, triggerId: newValue });
      } else {
        triggerDropdown.defaultValue = newValue;
      }
      taskDescriptionQuestion.defaultValue = taskDescription.current;

      setSelectedTrigger(newValue);
      setFormStructure(updatedFormStructure);
    }

    if (questionName === "taskId" && newValue) {
      const updatedFormStructure = { ...formStructure };
      const taskDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "taskId"
      );
      const taskDescriptionQuestion =
        updatedFormStructure.pages[0].elements.find(
          (element) => element.name === "taskDescription"
        );
      const task = libraryTasksData.find((t) => t.id === newValue);
      if (taskDescriptionQuestion) {
        setTaskDescription(task?.description ?? "");
        taskDescriptionQuestion.defaultValue = taskDescription.current;
      }

      if (taskDropdown) {
        taskDropdown.choices = libraryTasksData.map((task) => ({
          value: task.id,
          text: task.name,
        }));
      }
      if (isUpdate) {
        setSelectedData({
          ...selectedData,
          taskId: newValue,
          taskDescription: task?.description ?? "",
        });
      } else {
        taskDropdown.defaultValue = newValue;
      }

      const postTaskDropdown = updatedFormStructure.pages[0].elements.find(
        (element) => element.name === "postCompletionTaskId"
      );

      if (postTaskDropdown) {
        postTaskDropdown.choices = postTasks.filter(
          (task) => task.value !== newValue
        );
      }

      setFormStructure(updatedFormStructure);
    }

    if (questionName === "taskDescription" && newValue) {
      setTaskDescription(newValue);
    }
  };

  const handlePanelChange = (sender, options) => {
    const questionName = options.name;
    const newValue = options.value;

    if (questionName === "questionKey") {
      const panel = options.panel;
      const conditionQuestion = panel.getQuestionByName("conditionId");
      let inputType = "";
      if (newValue) {
        inputType =
          newValue.split("#")[1] === "null" ||
          newValue.split("#")[1] === undefined
            ? "Text"
            : newValue.split("#")[1];
      }

      conditionQuestion.choices = creationInfoData.allowedConditions
        .filter((choice) => {
          const dataType =
            choice.dataType === "String" ? "Text" : choice.dataType;

          if (
            (inputType === "Date" ||
              inputType === "Time" ||
              inputType === "DateTime") &&
            dataType === "Datetime"
          ) {
            return choice;
          }

          if (choice.dataType === inputType) {
            return choice;
          }
          return false;
        })
        .map((c) => ({
          value: `${c?.id}#${c.dataType}`,
          text: c?.name,
        }));

      panel.getQuestionByName("value").inputType = inputType;
    }
  };

  const handleAfterPanelRender = (sender, options) => {
    const panel = options.panel;
    const questionValue = panel.getQuestionByName("questionKey");
    const conditionQuestion = panel.getQuestionByName("conditionId");
    const inputType = questionValue?.value?.split("#")[1] ?? "text";

    conditionQuestion.choices = creationInfoData.allowedConditions
      .filter((choice) => {
        const dataType =
          choice.dataType === "String" ? "Text" : choice.dataType;

        if (
          (inputType === "Date" ||
            inputType === "Time" ||
            inputType === "DateTime") &&
          dataType === "Datetime"
        ) {
          return choice;
        }

        if (choice.dataType === inputType) {
          return choice;
        }
        return false;
      })
      .map((c) => ({
        value: `${c?.id}#${c.dataType}`,
        text: c?.name,
      }));
    panel.getQuestionByName("value").inputType = inputType;
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedData({});
    setIsUpdate(false);
    setReadOnly(false);
  };

  const openModal = () => {
    setModalOpen(true);
  };

  const handleSave = async (formData) => {
    const conditions = (formData?.conditions || []).map((condition) => {
      const questionKey = condition.questionKey.split("#")[0];
      const conditionIdStr = condition.conditionId.split("#")[0];
      const conditionId = parseInt(conditionIdStr, 10);
      if (isNaN(conditionId)) {
        throw new Error(`Invalid conditionId: ${conditionIdStr}`);
      }
      return {
        ...condition,
        questionKey,
        conditionId,
        value: condition.value.toString(),
      };
    });

    const assignedTo = (formData.assignedTo || [])
      .map((assignee) => {
        const splitId = assignee.split("-");
        if (splitId.length !== 2) {
          console.warn("Invalid assignee format:", assignee);
          return null;
        }
        return { [splitId[0]]: splitId[1] };
      })
      .filter(Boolean);

    const formDataFormatted = {
      ...formData,
      assignedTo,
      conditions,
    };

    try {
      if (!isUpdate) {
        await createIncidentTaskTrigger(formDataFormatted).unwrap();
        toast.success("New Task Automation Created Successfully");
      } else {
        await updateIncidentTaskTrigger(formDataFormatted).unwrap();
        toast.success("Task Automation Updated Successfully"); // Fixed typo
      }
      closeModal();
      resetForm();
    } catch (error) {
      const message =
        error?.data?.Message || "Request failed. Please try again.";
      toast.error(message);
      console.error("Save error:", error);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteIncidentTaskTrigger(selectedData?.id).unwrap();
      toast.success("Task Automation deleted successfully");
      setShowModal(false);
    } catch (error) {
      toast.error(error?.data?.Message);
    }
  };

  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen);
  };

  let gridRef = useRef(null);

  const handleSearch = (e) => {
    const grid = gridRef.current;
    const inputValue = sanitizeInputValue(e.target.value);
    grid?.search(inputValue);
  };

  const handleNewTask = () => {
    setIsUpdate(false);
    openModal();
  };

  return (
    <div className="incident-tasks">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: "#0b463d",
            color: "#ffffff",
          },
          success: {
            iconTheme: {
              secondary: "#ffffff",
            },
          },
        }}
      />
      <div className="row mb-1 justify-content-between align-items-center">
        <div className="col-auto">
          <h4 className="p-2 m-0">Task Automations </h4>
        </div>
        <div className="col-auto">
          <button
            className="btn btn-green btn-icon px-5"
            onClick={handleNewTask}
          >
            <span className="me-2"> New Task Automation</span>
            <img src={addIcon} width={22} alt="" />
          </button>
        </div>
      </div>
      <div className="card custom-card incident-details-card">
        <div className="card-body">
          <div className="row mb-3">
            <div className="col">
              <div className="card custom-card img-card banner">
                <div className="">
                  <div className="card-body custom-card-body">
                    <p className="fs-2">Task Automation</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row justify-content-between align-items-center mb-3">
            <div className="col-auto">
              <h6>Task Automation List</h6>
            </div>
            <div className="col-auto">
              <div className="d-flex align-items-center">
                <div className="input-group">
                  <label
                    htmlFor="searchIncident"
                    className="input-group-text search-icon-container"
                  >
                    <img
                      src={searchIcon}
                      alt="Search Icon"
                      className="icon-image"
                      width={12}
                    />
                  </label>
                  <input
                    id="searchIncident"
                    type="text"
                    className="form-control search"
                    placeholder="Search Task Automations"
                    onKeyUp={handleSearch}
                  />
                </div>
                <button className="btn btn-filter" onClick={toggleModal}>
                  <img src={filterIcon} width={34} alt="" />
                </button>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {(incidentTaskCreationTriggersIsLoading ||
                creationInfoDataIsLoading ||
                creationInfoDataIsFetching ||
                // usersIsLoading ||
                incidentGroupsIsLoading) && <Loader />}
              {incidentTaskCreationTriggersIsSuccess &&
                (incidentTaskCreationTriggersData.length > 0 ? (
                  <DataTable
                    data={incidentTaskCreationTriggersData}
                    columns={columns}
                    height={"calc(100vh - 555px)"}
                    grid={gridRef}
                  />
                ) : (
                  "No Task Automations Available..."
                ))}
            </div>
          </div>
        </div>
      </div>
      <FilterCoreModal isOpen={modalIsOpen} toggle={toggleModal}>
        <FilterForm />
      </FilterCoreModal>

      <IncidentReportingModal
        isOpen={modalOpen}
        toggle={closeModal}
        title={
          isUpdate
            ? `Update Task Automation "${selectedData?.name}"`
            : readOnly
            ? `${selectedData?.name}`
            : "Create New Task Automation"
        }
      >
        {createIncidentTaskTriggerIsLoading ||
        updateIncidentTaskTriggerIsLoading ? (
          <div className="mb-5">
            <Loader />
          </div>
        ) : (
          <DataForm
            readOnly={readOnly}
            data={formStructure}
            handleSave={handleSave}
            handleTrigger={handleTrigger}
            handlePanelChange={handlePanelChange}
            handleAfterPanelRender={handleAfterPanelRender}
            formData={isUpdate || readOnly ? selectedData : null}
            assignedToVariables={assignedToVariables}
          />
        )}
      </IncidentReportingModal>
      <DeleteConfirmationModal
        type={`Task Automation`}
        showModal={showModal}
        closeModal={setShowModal}
        confirmDelete={handleDelete}
        loading={deleteIncidentTaskTriggerIsLoading}
      />
    </div>
  );
};

export default TaskAutomation;
